/**
 * Session Management for Public Booking
 *
 * Manages browser session IDs for seat holds and booking flow.
 * Sessions are tenant-specific and stored in localStorage.
 */

/**
 * Generate a UUID v4 for session identification
 */
function generateUUID(): string {
  // Use crypto.randomUUID if available (modern browsers)
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get the localStorage key for a tenant's session
 */
function getSessionKey(slug: string): string {
  return `rutalink_session_${slug}`;
}

/**
 * Get or create a session ID for the given tenant
 * Creates a new UUID if no session exists
 *
 * @param slug - Tenant slug identifier
 * @returns Session UUID
 */
export function getSessionId(slug: string): string {
  if (!slug) {
    throw new Error("Tenant slug is required for session management");
  }

  const key = getSessionKey(slug);
  let sessionId = localStorage.getItem(key);

  if (!sessionId) {
    sessionId = generateUUID();
    localStorage.setItem(key, sessionId);
  }

  return sessionId;
}

/**
 * Clear the session for a given tenant
 * Used when session expires or booking is completed
 *
 * @param slug - Tenant slug identifier
 */
export function clearSession(slug: string): void {
  if (!slug) {
    return;
  }

  const key = getSessionKey(slug);
  localStorage.removeItem(key);
}

/**
 * Check if a session exists for the given tenant
 *
 * @param slug - Tenant slug identifier
 * @returns true if session exists
 */
export function hasSession(slug: string): boolean {
  if (!slug) {
    return false;
  }

  const key = getSessionKey(slug);
  return localStorage.getItem(key) !== null;
}
